/**
 * briefingPrompt — the prompts behind the AI Sentry Briefing (short + expanded) and the light
 * page's "Ask EdgeNotary" panel. One builder for /ai-sentry and /ai-sentry-light (WP11).
 *
 * Tone (Brian, Foster Rd demo cleanup, WP11): factual and calm. Describe what was observed and
 * group related activity ("Two related activities detected: reconnaissance from an unknown
 * device, then control commands from the historian"), not "site is under active multi-vector
 * attack". Recommendations say "Recommend …", never an imperative "Isolate immediately".
 *
 * The edge agent's own traffic: see utils/edgeAgentAddresses.js (the annotation EDGE_AGENT_ADDRESSES,
 * why it is maintained by hand, and its checks). Re-exported below for existing importers.
 */
import { eventSourceLabel, isAgentOriginated } from './eventSource';
import { eventAction, MONITOR_MODE_PROMPT } from './monitorMode';
import { titleCaseEdgeNodeId } from './edgeNodeDisplayName';
import { computeCounts } from './counts';
import { EDGE_AGENT_ADDRESSES } from './edgeAgentAddresses';

export { EDGE_AGENT_ADDRESSES };

/** Sent with a briefing request: the backend answers from the prompt alone (chat_router). */
export const SITE_BRIEFING_MODE = 'site_briefing';

/** The two values of a row's origin, in words the model may repeat. */
export const ORIGIN_EDGE_AGENT = "the edge agent's own monitoring (polls and discovery scans)";
export const ORIGIN_OBSERVED = 'observed on the network';
/** A device's traffic TO the edge agent: its answer to the agent's polls and discovery scans. */
export const ORIGIN_AGENT_REPLY = "a device's reply to the edge agent's monitoring (sent from the device to the edge agent)";

/**
 * The three openings of the short briefing, decided here rather than by the model. There is no
 * "All clear": the Briefing sits beside a Findings tile and an Alerts tile, and an opening that
 * reassured while those read 1-2 findings and hundreds of alerts would contradict the screen
 * (coordinator ruling on PR #244, B1).
 */
export const OPENING_ATTENTION = 'Attention needed:';
export const OPENING_REVIEW = 'Review suggested:';
export const OPENING_NO_CRITICAL = 'No critical activity observed on the network:';
const ATTENTION_SEVERITIES = ['Critical', 'High'];

const SEVERITY_ORDER = { Critical: 0, High: 1, Warning: 2, Notice: 3, Info: 4, Informational: 4 };
const sevRank = (sev) => SEVERITY_ORDER[sev] ?? 5;

/** The site's edge agent addresses: the annotation plus any source on an agent-flagged event. */
export function edgeAgentAddresses(siteId, events) {
  const addrs = new Set(EDGE_AGENT_ADDRESSES[siteId] || []);
  (events || []).forEach((e) => {
    if (e && e.source_ip && e.source_ip !== 'N/A' && isAgentOriginated(e)) addrs.add(e.source_ip);
  });
  return addrs;
}

/** True when the event is the edge agent's own traffic (flagged, or from its address). */
export function isEdgeAgentTraffic(e, agentAddrs) {
  return isAgentOriginated(e) || Boolean(e && e.source_ip && agentAddrs && agentAddrs.has(e.source_ip));
}

/** True when the event is a device's traffic to the edge agent (not the agent's own). */
export function isReplyToEdgeAgent(e, agentAddrs) {
  return !isEdgeAgentTraffic(e, agentAddrs)
    && Boolean(e && e.destination_ip && agentAddrs && agentAddrs.has(e.destination_ip));
}

/** The row's origin phrase: the agent's own traffic, a device's reply to it, or observed traffic. */
export function rowOrigin(e, agentAddrs) {
  if (isEdgeAgentTraffic(e, agentAddrs)) return ORIGIN_EDGE_AGENT;
  if (isReplyToEdgeAgent(e, agentAddrs)) return ORIGIN_AGENT_REPLY;
  return ORIGIN_OBSERVED;
}

/**
 * One event as sent to the model. No internal flag names: the origin is a phrase. The edge agent is
 * named as such on whichever end it is, so a device's reply to the agent reads "device -> Edge agent".
 */
export function briefingRow(e, agentAddrs) {
  const origin = rowOrigin(e, agentAddrs);
  const src = origin === ORIGIN_EDGE_AGENT && e.source_ip ? `Edge agent (${e.source_ip})` : eventSourceLabel(e);
  const dst = origin === ORIGIN_AGENT_REPLY ? `Edge agent (${e.destination_ip})` : e.destination_ip;
  return {
    source: src,
    origin,
    destination: dst,
    severity: e.severity,
    policy: (e.details && e.details.policy_name) || e.event_type,
    protocol: e.protocol,
    action: eventAction(e) || undefined,
  };
}

/**
 * The findings the screen shows: the caller's counts (utils/counts.js computeCounts, the same object
 * the Findings tile reads) when given, else computed from the same events by the same function.
 */
export function screenFindings(siteId, events, counts) {
  if (counts && Array.isArray(counts.findingList)) return counts.findingList;
  return computeCounts({ events: events || [], siteId }).findingList;
}

/** True when a finding's source is the site's edge agent (its own monitoring traffic). */
export function isEdgeAgentFinding(f, agentAddrs) {
  return Boolean(f && f.device && agentAddrs && agentAddrs.has(f.device));
}

/**
 * The short briefing's opening, decided deterministically:
 *   "Attention needed:"  any row OBSERVED on the network (not the edge agent's own traffic, not a
 *                        device's reply to it) is Critical or High;
 *   "Review suggested:"  else, any finding on the screen whose source is not the edge agent;
 *   "No critical activity observed on the network:"  else (no findings, or every finding's source is
 *                        the edge agent itself, which the prompt then asks the body to say).
 * On dev, ADC's only Critical rows were the agent's polls matching an IT-to-OT rule.
 */
export function briefingOpening(siteId, events, counts) {
  const agentAddrs = edgeAgentAddresses(siteId, events);
  const attention = (events || []).some((e) => e
    && rowOrigin(e, agentAddrs) === ORIGIN_OBSERVED
    && ATTENTION_SEVERITIES.includes(e.severity));
  if (attention) return OPENING_ATTENTION;
  const findings = screenFindings(siteId, events, counts);
  if (findings.some((f) => !isEdgeAgentFinding(f, agentAddrs))) return OPENING_REVIEW;
  return OPENING_NO_CRITICAL;
}

/** The screen's findings as sent to the model, and the instruction to mention each one. */
function findingsBlock(siteId, events, counts) {
  const agentAddrs = edgeAgentAddresses(siteId, events);
  const findings = screenFindings(siteId, events, counts);
  if (findings.length === 0) return 'Findings shown on screen: none.\n\n';
  const rows = findings.map((f) => {
    const agent = isEdgeAgentFinding(f, agentAddrs);
    return {
      source: agent ? `Edge agent (${f.device})` : f.device,
      origin: agent ? ORIGIN_EDGE_AGENT : ORIGIN_OBSERVED,
      finding: f.reason,
      targets: (f.targetDevices || []).length,
    };
  });
  const agentNote = rows.some((r) => r.origin === ORIGIN_EDGE_AGENT)
    ? " A finding whose source is the edge agent comes from its own monitoring traffic (its polls and discovery scans matching a pattern): say so plainly, as a monitoring note, not as a threat."
    : '';
  return `Findings shown on screen (${findings.length}, the same list as the Findings tile):\n${JSON.stringify(rows, null, 2)}\n`
    + `Mention every finding listed here; do not leave any out.${agentNote}\n\n`;
}

const OPENING_REASON = {
  [OPENING_ATTENTION]: 'traffic observed on the network includes Critical or High events',
  [OPENING_REVIEW]: "no traffic observed on the network is Critical or High, but the screen shows findings that are not the edge agent's own monitoring",
  [OPENING_NO_CRITICAL]: "no traffic observed on the network is Critical or High, and every finding on the screen, if any, is the edge agent's own monitoring; Critical or High rows from the edge agent's own monitoring, or replies to it, do not change this",
};

/**
 * The site's activity as at most n rows. Identical events (same source, destination, policy,
 * severity, protocol and action) are one row with a count and first/last time, so a repeating
 * poll cannot crowd distinct activity out of the window. Order: everything observed on the network
 * before the edge agent's own traffic and the devices' replies to it (on dev most Critical Foster rows were the agent's, and would
 * otherwise fill the window), then severity, then count. Does not mutate the caller's array.
 */
export function briefingRows(siteId, events, n) {
  const agentAddrs = edgeAgentAddresses(siteId, events);
  const groups = new Map();
  (events || []).forEach((e) => {
    if (!e) return;
    const row = briefingRow(e, agentAddrs);
    const key = JSON.stringify(row);
    const g = groups.get(key);
    const ts = e.timestamp || null;
    if (!g) {
      groups.set(key, { ...row, count: 1, first_seen: ts, last_seen: ts });
      return;
    }
    g.count += 1;
    if (ts && (!g.first_seen || ts < g.first_seen)) g.first_seen = ts;
    if (ts && (!g.last_seen || ts > g.last_seen)) g.last_seen = ts;
  });
  const isAgent = (r) => (r.origin === ORIGIN_OBSERVED ? 0 : 1);
  return [...groups.values()]
    .sort((a, b) => isAgent(a) - isAgent(b)
      || sevRank(a.severity) - sevRank(b.severity)
      || b.count - a.count)
    .slice(0, n);
}

const agentPhrase = (addrs) => (addrs.size ? ` (address ${[...addrs].join(', ')})` : '');

/** The tone and edge-agent rules every briefing and chat answer carries. */
export function toneRules(siteName, agentAddrs) {
  const at = agentPhrase(agentAddrs || new Set());
  return [
    'How to write:',
    '- Be factual and calm. Describe what was observed, in plain operational language. Do not use alarm phrases such as "under attack", "multi-vector", "attack campaign", "breach" or "compromised"; say what the events show.',
    '- Group related activity into one item and say how the parts relate, for example: "Two related activities detected: reconnaissance from an unknown device, then control commands from the historian."',
    `- Cover ${siteName} only. Name only devices and addresses that appear in the data above; never mention other sites.`,
    '- Write every recommendation as "Recommend ..." (for example "Recommend confirming whether the unknown device is authorized"). Never write an imperative command such as "Isolate immediately", "Block now" or "Contain".',
    '- Each row is traffic sent from its source to its destination. Keep that direction when you describe it; never swap the two ends.',
    `- Events whose origin is the edge agent's own monitoring come from the monitoring system itself${at}: its routine polls and discovery scans. Describe them as routine monitoring, never as an attack, reconnaissance by an intruder or lateral movement, and never recommend containing, isolating or restricting the edge agent${at}. If its own traffic matched a policy, say so as a policy-tuning note.`,
    `- Events whose origin is a device's reply to the edge agent's monitoring were sent by that device to the edge agent${at}, answering its polls and discovery scans. Describe them as the device replying to the edge agent's monitoring (for example "the relay replied to the edge agent's polls"), not as the edge agent sending traffic, and as routine; a policy match on them is a policy-tuning note.`,
    '- Never mention field names, JSON keys or internal identifiers; write in plain language.',
  ].join('\n');
}

function siteBlock(siteId, events, n, heading) {
  const siteName = siteId ? titleCaseEdgeNodeId(siteId) : 'all monitored sites';
  const rows = briefingRows(siteId, events, n);
  const site = siteId ? `${siteId} (${siteName})` : siteName;
  return { siteName, text: `Site: ${site}\n${heading} (up to ${n} rows, identical events counted together; observed traffic first, most severe first):\n${JSON.stringify(rows, null, 2)}\n\n` };
}

/** The short Operator Summary prompt. */
export function buildShortBriefingPrompt(siteId, events, counts) {
  const { siteName, text } = siteBlock(siteId, events, 10, 'Recent events');
  const opening = briefingOpening(siteId, events, counts);
  const why = OPENING_REASON[opening];
  return `${text}${findingsBlock(siteId, events, counts)}${MONITOR_MODE_PROMPT}${toneRules(siteName, edgeAgentAddresses(siteId, events))}

Task: write a 2-3 sentence shift change briefing for ${siteName}.
Begin with exactly "${opening}" (${why}).
Summarize the related activity in order of significance (severity, then event count), naming the source and target devices.
End with one recommendation that begins with "Recommend".`;
}

/** The Expanded Analysis prompt. The UI renders the reply as markdown; keep the headings. */
export function buildExpandedBriefingPrompt(siteId, events, counts) {
  const { siteName, text } = siteBlock(siteId, events, 20, 'Event data');
  return `${text}${findingsBlock(siteId, events, counts)}${MONITOR_MODE_PROMPT}${toneRules(siteName, edgeAgentAddresses(siteId, events))}

Task: write a security analysis for ${siteName}, in markdown, with these sections:
1. **Summary**: What was observed, grouped into related activities.
2. **Assets Involved**: Which devices and addresses at this site are involved, and their roles.
3. **Timeline**: When each related activity started and how they connect, with timestamps from the event data.
4. **Assessment**: How significant this is, stated plainly.
5. **NERC CIP Relevance**: Which CIP controls are relevant. Only include if the activity touches substation, generation, or transmission control assets.
6. **Monitoring Notes**: Any policy matches caused by the edge agent's own monitoring traffic or by the devices' replies to it, as tuning items.
7. **Recommendations**: A short list; each item begins with "Recommend".

Use the IP addresses, protocols and timestamps from the event data above.`;
}

/** Rules appended to the "Ask EdgeNotary" panel's context. */
export function chatToneRules(siteId) {
  const siteName = siteId ? titleCaseEdgeNodeId(siteId) : 'the selected site';
  return toneRules(siteName, edgeAgentAddresses(siteId, []));
}
